import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  LogOut,
  Check,
  Building2,
  DollarSign,
  UserPlus,
  AlertCircle,
  Settings,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Notification {
  id: string;
  user_id: string;
  tenant_id: string | null;
  interview_session_id: string | null;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

export const SystemAdminHeader: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Fetch unread notification count
  const fetchUnreadCount = async () => {
    if (!user?.id) return;

    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error fetching unread count:', error);
      } else {
        setUnreadCount(count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Fetch notifications for system admin
  const fetchNotifications = async () => {
    if (!user?.id) {
      setNotificationsLoading(false);
      return;
    }

    setNotificationsLoading(true);
    try {
      // Fetch both notifications and count in parallel
      const [notificationsResponse] = await Promise.all([
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_read', false)
          .order('created_at', { ascending: false })
          .limit(20),
        fetchUnreadCount(), // Fetch accurate count separately
      ]);

      const { data, error } = notificationsResponse;

      if (error) {
        console.error('Error fetching notifications:', error);
      } else {
        setNotifications(data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    // Early return if no user - don't create any subscriptions
    if (!user?.id) {
      return;
    }

    fetchNotifications();

    // Set up real-time subscription for new notifications
    const channel = supabase
      .channel('system-admin-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        payload => {
          const newNotification = payload.new as Notification;
          setNotifications(prev => [newNotification, ...prev].slice(0, 20));

          // Increment unread count when new notification arrives
          setUnreadCount(prev => prev + 1);

          // Show toast for important notifications
          if (
            [
              'tenant_invitation_accepted',
              'billing_payment_failed',
              'system_maintenance_scheduled',
            ].includes(newNotification.type)
          ) {
            toast.info(newNotification.title, {
              description: newNotification.message,
              duration: 5000,
            });
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR') {
          console.debug('Realtime subscription error (this is normal in local development):', err);
        } else if (status === 'SUBSCRIBED') {
          console.debug('Successfully subscribed to system admin notifications');
        }
      });

    return () => {
      // Safely remove channel with proper async error handling
      if (channel) {
        supabase.removeChannel(channel).catch(() => {
          // Silently handle WebSocket cleanup errors during component unmount
          // This is normal during rapid auth state changes or browser navigation
        });
      }
    };
  }, [user?.id]); // Use stable user ID instead of user object to prevent rapid re-subscriptions

  // Mark a single notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
      } else {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Decrement unread count when marking as read
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return;

    // Don't check notifications.length === 0 because there might be more unread notifications
    // in the database than what's loaded in the UI due to pagination limits

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        toast.error('Failed to mark all notifications as read');
      } else {
        // Refresh notifications from database to get accurate state
        await fetchNotifications();
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  // Handle notification click based on type
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    await markAsRead(notification.id);

    // Navigate based on notification type
    switch (notification.type) {
      case 'tenant_invitation_accepted':
        if (notification.metadata?.tenant_id) {
          navigate(`/system-admin/tenants`);
        }
        break;
      case 'billing_payment_failed':
      case 'billing_payment_succeeded':
      case 'billing_subscription_created':
      case 'billing_subscription_cancelled':
        navigate('/system-admin/billing');
        break;
      case 'system_maintenance_scheduled':
      case 'system_update_available':
        navigate('/system-admin/settings');
        break;
      case 'interview_invitation_expired':
        // Navigate to position details to show expired invitation
        if (notification.metadata?.position_id) {
          navigate(`/positions/${notification.metadata.position_id}#candidates`);
        } else {
          // Fallback to invitations management
          navigate('/system-admin/invitations');
        }
        break;
      case 'interview_feedback_received':
        // Handle interview assessment notifications
        if (notification.metadata) {
          try {
            // Extract session_id from notification metadata
            const sessionId = notification.metadata.session_id || notification.interview_session_id;

            if (sessionId) {
              // Get position_id from the interview session
              const { data: sessionData, error } = await supabase
                .from('interview_sessions')
                .select('position_id, candidate_id')
                .eq('id', sessionId)
                .single();

              if (error || !sessionData) {
                console.error('Error fetching session data:', error);
                toast.error('Unable to find interview session');
                return;
              }

              // Navigate to position details with candidates tab active
              // Pass candidate_id and session_id as URL parameters to trigger assessment modal
              navigate(
                `/positions/${sessionData.position_id}?candidateId=${sessionData.candidate_id}&sessionId=${sessionId}&showAssessment=true#candidates`
              );
              return;
            }
          } catch (error) {
            console.error('Error handling interview assessment notification:', error);
            toast.error('Unable to navigate to assessment');
          }
        }
        break;
      case 'job_board_application_needs_review':
      case 'job_board_application_high_score':
        // Navigate to specific candidate profile for job board applications
        if (notification.metadata?.candidate_id) {
          navigate(`/candidates/${notification.metadata.candidate_id}`);
        } else {
          // Fallback to candidates management
          navigate('/system-admin/candidates');
        }
        break;
      default:
        // For other interview-related notifications
        if (notification.interview_session_id) {
          navigate(`/system-admin/sessions`);
        }
    }
  };

  // Get icon for notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'tenant_invitation_accepted':
      case 'tenant_created':
        return <Building2 className="h-4 w-4 text-blue-500" />;
      case 'billing_payment_failed':
      case 'billing_payment_succeeded':
      case 'billing_subscription_created':
      case 'billing_subscription_cancelled':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      case 'user_invitation_accepted':
      case 'user_deactivated':
        return <UserPlus className="h-4 w-4 text-purple-500" />;
      case 'system_maintenance_scheduled':
      case 'system_update_available':
        return <Settings className="h-4 w-4 text-orange-500" />;
      case 'interview_invitation_expired':
        return <Bell className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get badge color for notification type
  const getNotificationBadgeColor = (
    type: string
  ): 'default' | 'destructive' | 'outline' | 'secondary' => {
    if (type.includes('failed') || type.includes('cancelled') || type.includes('expired')) {
      return 'destructive';
    }
    if (type.includes('accepted') || type.includes('succeeded') || type.includes('created')) {
      return 'default';
    }
    return 'secondary';
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    const firstName = user?.user_metadata?.first_name;
    const lastName = user?.user_metadata?.last_name;

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (lastName) {
      return lastName[0].toUpperCase();
    }
    return user?.email?.[0].toUpperCase() || 'A';
  };

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">System Administration</h1>
          <p className="text-sm text-gray-500">Manage all tenants and system resources</p>
        </div>

        <div className="flex items-center gap-4">
          {/* Notification Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-96">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>System Notifications</span>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    onClick={e => {
                      e.preventDefault();
                      markAllAsRead();
                    }}
                  >
                    Mark all as read
                  </Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notificationsLoading ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Loading notifications...
                </div>
              ) : notifications.length > 0 ? (
                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.map(notification => (
                    <DropdownMenuItem
                      key={notification.id}
                      className="flex items-start gap-3 p-3 cursor-pointer border-b last:border-0 hover:bg-blue-50 focus:bg-blue-50 hover:text-gray-900 focus:text-gray-900"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-start gap-2 flex-wrap">
                          <p className="text-sm font-medium text-gray-900 flex-shrink-0">
                            {notification.title}
                          </p>
                          <Badge
                            variant={getNotificationBadgeColor(notification.type)}
                            className="text-[10px] px-1.5 py-0 h-5 flex-shrink-0"
                          >
                            {notification.type
                              .split('_')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 break-words">{notification.message}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0 hover:bg-gray-100"
                        onClick={e => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                    </DropdownMenuItem>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Bell className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No new notifications</p>
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.user_metadata?.avatar_url || undefined} />
                  <AvatarFallback className="text-sm">{getUserInitials()}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium truncate max-w-[200px]">{user?.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>System Administrator</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/system-admin/profile')}>
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/system-admin/settings')}>
                System Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600 cursor-pointer" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
